import React, { createContext, useContext, useMemo } from 'react';
import { useLazyBoardWithQuery } from '../hooks/board';
import { useColumnInfiniteQuery } from '../hooks/queries';
// Data Context - Only rerenders when board data changes
const BoardDataContext = createContext(null);

// Actions Context - Never rerenders (stable functions)
const BoardActionsContext = createContext(null);

// State Context - Loading states, errors, etc.
const BoardStateContext = createContext(null);

/**
 * Hook to access board data (columns, tasks, pagination)
 * Only rerenders when board data changes
 */
export const useBoardData = () => {
  const context = useContext(BoardDataContext);
  if (!context) {
    throw new Error('useBoardData must be used within BoardProvider');
  }
  return context;
};

/**
 * Hook to access board actions (moveCard, reorderColumns, etc.)
 * Never triggers rerenders (functions are stable)
 */
export const useBoardActions = () => {
  const context = useContext(BoardActionsContext);
  if (!context) {
    throw new Error('useBoardActions must be used within BoardProvider');
  }
  return context;
};

/**
 * Hook to access board state (loading, errors, operation status)
 * Only rerenders when state changes
 */
export const useBoardState = () => {
  const context = useContext(BoardStateContext);
  if (!context) {
    throw new Error('useBoardState must be used within BoardProvider');
  }
  return context;
};

/**
 * Optimized selector hook - Subscribe to specific column data only
 * Only rerenders when the specific column's data changes
 */
export const useColumnData = (columnId) => {
  const { boardState } = useBoardData();

  return useMemo(() => {
    if (!boardState || !columnId) {
      return { items: [], pagination: null };
    }

    return {
      items: boardState.tasks?.[columnId] || [],
      pagination: boardState.pagination?.[columnId] || null,
    };
  }, [boardState, columnId]);
};

/**
 * Hook to check if an entity is locked
 * Optimized to only rerender when the specific entity's lock state changes
 */
export const useIsEntityLocked = (entityId) => {
  const { lockedEntities } = useBoardState();
  return useMemo(() => {
    return lockedEntities.includes(entityId);
  }, [lockedEntities, entityId]);
};

/**
 * SIMPLIFIED infinite scroll hook for a specific column - SINGLE CACHE
 *
 * Uses React Query's infinite query directly - ALL pages in one cache.
 * Page 1 is pre-loaded by useInitialBoardLoad, subsequent pages fetched on scroll.
 *
 * Benefits:
 * - No dual-cache complexity
 * - No merge logic needed
 * - Single source of truth
 * - Built-in pagination state
 *
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Filter parameters
 * @returns {Object} - Flattened items, pagination controls, and loading states
 */
export const useColumnInfiniteData = (columnId, filters = {}) => {
  const infiniteQuery = useColumnInfiniteQuery(columnId, filters);

  // Flatten all pages into single array
  const allItems = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.items) || [];
  }, [infiniteQuery.data]);

  // Get total count from first page
  const totalCount = useMemo(() => {
    return infiniteQuery.data?.pages[0]?.totalCount || 0;
  }, [infiniteQuery.data]);

  const isActuallyLoading = infiniteQuery.isLoading && infiniteQuery.isFetching;

  return {
    items: allItems,
    totalCount,
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isLoading: isActuallyLoading, // Only show loading if actively fetching
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
  };
};

/**
 * Main Board Provider Component
 * Provides optimized context with minimal rerenders
 */
export const BoardProvider = ({ children, filters = {} }) => {
  const boardData = useLazyBoardWithQuery(filters);

  // Memoize data context value - only changes when task stages or practice areas change
  const dataContextValue = useMemo(
    () => ({
      // REMOVED: boardState (no longer needed with single-cache architecture)
      taskStages: boardData.taskStages,
      practiceAreaList: boardData.practiceAreaList,
      isPracticeAreaLoading: boardData.isPracticeAreaLoading,
      userList: boardData.userList,
      isLoadingUsers: boardData.isLoadingUsers,
      clientList: boardData.clientList,
      isLoadingClients: boardData.isLoadingClients,
    }),
    [
      boardData.taskStages,
      boardData.practiceAreaList,
      boardData.isPracticeAreaLoading,
      boardData.userList,
      boardData.isLoadingUsers,
      boardData.clientList,
      boardData.isLoadingClients,
    ]
  );

  // Memoize actions context value - stable reference (functions are already stable from useCallback)
  // Empty deps array because these functions are stable and don't need to trigger rerenders
  const actionsContextValue = useMemo(
    () => ({
      moveCard: boardData.moveCard,
      reorderColumns: boardData.reorderColumns,
      // REMOVED: loadMoreTasks (handled by fetchNextPage in useColumnInfiniteData)
      updateCard: boardData.updateCard,
      moveCardToStage: boardData.moveCardToStage,
      renameColumn: boardData.renameColumn,
      deleteColumn: boardData.deleteColumn,
      duplicateColumn: boardData.duplicateColumn,
      resetBoard: boardData.resetBoard,
      createTaskStage: boardData.createTaskStage,
      checkStageExists: boardData.checkStageExists,
      // Task actions (remove/archive)
      removeTask: boardData.removeTask,
      moveTaskToDoneStage: boardData.moveTaskToDoneStage,
      findDoneStage: boardData.findDoneStage,
      // Task CRUD operations
      createTask: boardData.createTask,
      updateFullTask: boardData.updateFullTask,
      deleteTask: boardData.deleteTask,
      updateSubtask: boardData.updateSubtask,
    }),
    [boardData.findDoneStage] // Empty deps - functions are stable
  );

  // Memoize state context value - only changes when loading/error states change
  const stateContextValue = useMemo(
    () => ({
      initialLoading: boardData.initialLoading,
      isRefetchingData: boardData.isRefetchingData,
      isLoadingTaskStages: boardData.isLoadingTaskStages,
      error: boardData.error,
      operationStatus: boardData.operationStatus,
      lockedEntities: boardData.lockedEntities,
      hasPendingOperations: boardData.hasPendingOperations,
      isEntityLocked: boardData.isEntityLocked,
    }),
    [
      boardData.initialLoading,
      boardData.isRefetchingData,
      boardData.isLoadingTaskStages,
      boardData.error,
      boardData.operationStatus,
      boardData.lockedEntities,
      boardData.hasPendingOperations,
      boardData.isEntityLocked,
    ]
  );

  return (
    <BoardStateContext.Provider value={stateContextValue}>
      <BoardActionsContext.Provider value={actionsContextValue}>
        <BoardDataContext.Provider value={dataContextValue}>{children}</BoardDataContext.Provider>
      </BoardActionsContext.Provider>
    </BoardStateContext.Provider>
  );
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useBoardData, useBoardActions, or useBoardState instead
 */
export const useBoardContext = () => {
  const data = useBoardData();
  const actions = useBoardActions();
  const state = useBoardState();

  return {
    ...data,
    ...actions,
    ...state,
  };
};
